import React, { useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Box, 
  Paper, 
  TextField, 
  Typography, 
  IconButton, 
  Tooltip,
  styled
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import TitleIcon from '@mui/icons-material/Title';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import ListIcon from '@mui/icons-material/List';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

interface MarkdownEditorProps {
  initialContent?: string;
  onChange?: (content: string) => void;
}

function LinkRenderer(props: any) {
  return (
    <a href={props.href} target="_blank" rel="noreferrer">
      {props.children}
    </a>
  );
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ 
  initialContent = '', 
  onChange 
}) => {
  const [content, setContent] = useState(initialContent);

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  const insertMarkdown = (type: string) => {
    let insertion = '';
    let cursorPosition = 0;
    
    switch (type) {
      case 'bold':
        insertion = '**bold text**';
        cursorPosition = 2;
        break;
      case 'italic':
        insertion = '*italic text*';
        cursorPosition = 1;
        break;
      case 'heading1':
        insertion = '# Heading 1';
        cursorPosition = 2;
        break;
      case 'heading2':
        insertion = '## Heading 2';
        cursorPosition = 3;
        break;
      case 'heading3':
        insertion = '### Heading 3';
        cursorPosition = 4;
        break;
      case 'blockquote':
        insertion = '> Blockquote';
        cursorPosition = 2;
        break;
      case 'code':
        insertion = '`code`';
        cursorPosition = 1;
        break;
      case 'codeblock':
        insertion = '```\ncode block\n```';
        cursorPosition = 4;
        break;
      case 'link':
        insertion = '[link text](url)';
        cursorPosition = 1;
        break;
      case 'image':
        insertion = '![alt text](image-url)';
        cursorPosition = 2;
        break;
      case 'list':
        insertion = '- List item';
        cursorPosition = 2;
        break;
      case 'numberedList':
        insertion = '1. Numbered item';
        cursorPosition = 3;
        break;
      default:
        return;
    }

    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + insertion + content.substring(end);
    
    handleContentChange(newContent);
    
    // Set cursor position after the state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorPosition, start + cursorPosition);
    }, 0);
  };

  // Styled components for markdown preview
  const StyledMarkdown = styled(Box)(({ theme }) => ({
    '& h1': {
      fontSize: '2rem',
      fontWeight: 700,
      marginBottom: '1rem',
    },
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: '0.75rem',
    },
    '& h3': {
      fontSize: '1.25rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
    },
    '& p': {
      marginBottom: '1rem',
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.divider}`,
      paddingLeft: '1rem',
      fontStyle: 'italic',
      margin: '1rem 0',
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      borderRadius: '0.25rem',
      padding: '0.125rem 0.25rem',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      borderRadius: '0.25rem',
      padding: '1rem',
      overflow: 'auto',
      margin: '1rem 0',
    },
    '& ul': {
      listStyleType: 'disc',
      paddingLeft: '1.25rem',
      marginBottom: '1rem',
    },
    '& ol': {
      listStyleType: 'decimal',
      paddingLeft: '1.25rem',
      marginBottom: '1rem',
    },
    '& li': {
      marginBottom: '0.25rem',
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      margin: '1rem 0',
    },
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Bold">
          <IconButton onClick={() => insertMarkdown('bold')} size="small">
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton onClick={() => insertMarkdown('italic')} size="small">
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading 1">
          <IconButton onClick={() => insertMarkdown('heading1')} size="small">
            <TitleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading 2">
          <IconButton onClick={() => insertMarkdown('heading2')} size="small">
            <Typography variant="h6">H2</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading 3">
          <IconButton onClick={() => insertMarkdown('heading3')} size="small">
            <Typography variant="subtitle1">H3</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Blockquote">
          <IconButton onClick={() => insertMarkdown('blockquote')} size="small">
            <FormatQuoteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Inline Code">
          <IconButton onClick={() => insertMarkdown('code')} size="small">
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Code Block">
          <IconButton onClick={() => insertMarkdown('codeblock')} size="small">
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link">
          <IconButton onClick={() => insertMarkdown('link')} size="small">
            <LinkIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Image">
          <IconButton onClick={() => insertMarkdown('image')} size="small">
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet List">
          <IconButton onClick={() => insertMarkdown('list')} size="small">
            <ListIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton onClick={() => insertMarkdown('numberedList')} size="small">
            <FormatListNumberedIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ display: 'flex', flex: 1, gap: 2 }}>
        <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          <TextField
            id="markdown-editor"
            multiline
            fullWidth
            variant="outlined"
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Enter markdown content..."
            sx={{ 
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
              }
            }}
          />
        </Paper>
        
        <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <StyledMarkdown>
            <Markdown
              components={{a: LinkRenderer}}
              remarkPlugins={[remarkGfm]}
            >
              {content}
            </Markdown>
          </StyledMarkdown>
        </Paper>
      </Box>
    </Box>
  );
};

export default MarkdownEditor; 